import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertCircle, FiCalendar, FiCheckCircle, FiInfo, FiRefreshCw, FiClock, FiX } from 'react-icons/fi';
import useIsMobile from '../hooks/useIsMobile';
import { getUserKey } from '../utils/userStorage';

const Calendar = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useI18n();
  const isMobile = useIsMobile();
  const [viewDate, setViewDate] = useState(new Date());
  const [postsByDate, setPostsByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    try {
      const projects = JSON.parse(localStorage.getItem(getUserKey('projects')) || '[]');
      const byDate = {};
      projects.forEach(proj => {
        if (!proj.isArchived) {
          (proj.posts || []).forEach(post => {
            if (!post.isArchived && post.date) {
              if (!byDate[post.date]) byDate[post.date] = [];
              byDate[post.date].push({ ...post, projectName: proj.name });
            }
          });
        }
      });
      setPostsByDate(byDate);
    } catch {}
  }, []);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthStr = `${year}-${String(month+1).padStart(2,'0')}`;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let firstDay = new Date(year, month, 1).getDay();
  if (firstDay === 0) firstDay = 7;
  firstDay -= 1;

  const monthNames = t('calendar.months');
  const weekDayNames = t('calendar.weekDays');

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getDateStr = d => `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  const monthPosts = Object.entries(postsByDate).filter(([d]) => d.startsWith(monthStr));
  const totalThisMonth = monthPosts.reduce((s, [, p]) => s + p.length, 0);
  const publishedThisMonth = monthPosts.reduce((s, [, posts]) => s + posts.filter(p => p.platforms?.some(pl => pl.status === 'published')).length, 0);

  const selectedPosts = selectedDate ? (postsByDate[selectedDate] || []) : [];

  const cardStyle = {
    background: isLight ? '#ffffff' : 'rgba(255,255,255,0.04)',
    border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
    borderRadius: '14px',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{ padding: isMobile ? '12px' : '24px', color: isLight ? '#0f172a' : '#f8fafc', minHeight: '100vh' }}
    >
      {/* Header */}
      <div style={{ marginBottom: '18px' }}>
        <h1 style={{ fontSize: isMobile ? '20px' : '26px', fontWeight: 800, margin: '0 0 4px', color: 'var(--accent-color)', display: 'flex', alignItems: 'center', gap: '9px' }}>
          <FiCalendar size={22} aria-hidden="true" /> {t('calendar.title')}
        </h1>
        <p style={{ margin: 0, opacity: 0.55, fontSize: '13px' }}>{t('calendar.subtitle')}</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{ ...cardStyle, padding: '10px 18px', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ opacity: 0.6 }}>{t('calendar.totalThisMonth')}:</span>
          <strong style={{ color: 'var(--accent-color)', fontSize: '16px' }}>{totalThisMonth}</strong>
        </div>
        <div style={{ ...cardStyle, padding: '10px 18px', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ opacity: 0.6 }}>{t('calendar.publishedThisMonth')}:</span>
          <strong style={{ color: '#22c55e', fontSize: '16px' }}>{publishedThisMonth}</strong>
        </div>
      </div>

      {/* Month navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => setViewDate(new Date(year, month - 1, 1))}
            style={{ width: '34px', height: '34px', borderRadius: '10px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#fff' : 'rgba(255,255,255,0.05)', color: isLight ? '#334155' : '#cbd5e1', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >‹</button>
          <h2 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px', fontWeight: 700, minWidth: '170px', textAlign: 'center' }}>
            {Array.isArray(monthNames) ? monthNames[month] : ''} {year}
          </h2>
          <button
            onClick={() => setViewDate(new Date(year, month + 1, 1))}
            style={{ width: '34px', height: '34px', borderRadius: '10px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#fff' : 'rgba(255,255,255,0.05)', color: isLight ? '#334155' : '#cbd5e1', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >›</button>
        </div>
        <button
          onClick={() => { setViewDate(new Date()); setSelectedDate(todayStr); }}
          style={{ padding: '7px 14px', borderRadius: '9px', border: 'none', background: 'var(--accent-color)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '12px', fontFamily: 'inherit' }}
        >
          {t('calendar.today')}
        </button>
      </div>

      {/* Calendar grid */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)' }}>
          {(Array.isArray(weekDayNames) ? weekDayNames : ['Mo','Tu','We','Th','Fr','Sa','Su']).map((d, i) => (
            <div key={i} style={{ padding: isMobile ? '8px 2px' : '10px 6px', textAlign: 'center', fontSize: '11px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.3px', borderRight: i < 6 ? (isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.03)') : 'none' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {cells.map((day, i) => {
            const dateStr = day ? getDateStr(day) : null;
            const dayPosts = dateStr ? (postsByDate[dateStr] || []) : [];
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;
            const isWeekend = (i % 7) >= 5;

            return (
              <div
                key={i}
                onClick={() => day && setSelectedDate(isSelected ? null : dateStr)}
                style={{
                  minHeight: isMobile ? '44px' : '88px',
                  padding: isMobile ? '4px 3px' : '6px',
                  borderRight: (i + 1) % 7 !== 0 ? (isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.03)') : 'none',
                  borderBottom: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.03)',
                  cursor: day ? 'pointer' : 'default',
                  background: isSelected
                    ? (isLight ? 'rgba(var(--accent-color-rgb),0.07)' : 'rgba(255,255,255,0.07)')
                    : isToday
                    ? (isLight ? '#eff6ff' : 'rgba(37,99,235,0.08)')
                    : isWeekend
                    ? (isLight ? '#fafafa' : 'rgba(255,255,255,0.01)')
                    : 'transparent',
                  outline: isSelected ? '2px solid var(--accent-color)' : 'none',
                  outlineOffset: '-2px',
                  transition: 'background 0.12s',
                }}
              >
                {day && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? '2px' : '4px' }}>
                      <span style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '10px' : '12px', fontWeight: isToday ? 800 : 500, background: isToday ? 'var(--accent-color)' : 'transparent', color: isToday ? '#fff' : isLight ? '#475569' : '#94a3b8', flexShrink: 0 }}>
                        {day}
                      </span>
                      {dayPosts.length > 0 && !isMobile && (
                        <span style={{ fontSize: '9px', background: 'rgba(var(--accent-color-rgb),0.15)', color: 'var(--accent-color)', borderRadius: '3px', padding: '1px 4px', fontWeight: 700 }}>
                          {dayPosts.length}
                        </span>
                      )}
                    </div>

                    {isMobile && dayPosts.length > 0 && (
                      <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', marginTop: '2px' }}>
                        {dayPosts.slice(0, 4).map((_, j) => (
                          <div key={j} style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--accent-color)', opacity: j === 3 && dayPosts.length > 4 ? 0.5 : 1 }} />
                        ))}
                      </div>
                    )}

                    {!isMobile && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {dayPosts.slice(0, 3).map((post, j) => {
                          const st = post.platforms?.[0]?.status;
                          const c = st === 'published' ? '#22c55e' : st === 'cancelled' ? '#ef4444' : st === 'moved' ? '#f59e0b' : '#3b82f6';
                          return (
                            <div key={j} style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '3px', background: `${c}15`, borderLeft: `2px solid ${c}`, color: isLight ? '#374151' : '#d1d5db', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4 }}>
                              <span style={{ opacity: 0.5, fontSize: '9px' }}>{post.projectName}: </span>{post.title}
                            </div>
                          );
                        })}
                        {dayPosts.length > 3 && (
                          <div style={{ fontSize: '9px', opacity: 0.4, paddingLeft: '5px' }}>+{dayPosts.length - 3} ta</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day panel */}
      <AnimatePresence>
        {selectedDate && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            style={{ marginTop: '16px', ...cardStyle, padding: '18px 20px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '15px' }}>
                <FiCalendar size={15} aria-hidden="true" /> {t('calendar.postsFor')}: <strong>{selectedDate}</strong>
              </h3>
              <button
                onClick={() => setSelectedDate(null)}
                aria-label="Close selected date"
                style={{ width: '30px', height: '30px', display: 'grid', placeItems: 'center', background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', padding: 0 }}
              ><FiX size={17} /></button>
            </div>

            {selectedPosts.length === 0 ? (
              <p style={{ margin: 0, opacity: 0.5, fontSize: '13px' }}>{t('calendar.noPostsDay')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {selectedPosts.map((post, i) => (
                  <div key={i} style={{ padding: '12px 14px', borderRadius: '10px', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.04)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', background: 'rgba(var(--accent-color-rgb),0.12)', color: 'var(--accent-color)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>{post.projectName}</span>
                      <span style={{ fontSize: '11px', opacity: 0.45 }}>{post.format}</span>
                    </div>
                    <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '13px' }}>{post.title}</p>
                    {post.idea && <p style={{ margin: '0 0 8px', fontSize: '12px', opacity: 0.6, display: 'flex', alignItems: 'flex-start', gap: '6px' }}><FiInfo size={14} style={{ flexShrink: 0, marginTop: '2px' }} />{post.idea}</p>}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {(post.platforms || []).map((pl, j) => {
                        const c = pl.status === 'published' ? '#22c55e' : pl.status === 'cancelled' ? '#ef4444' : pl.status === 'moved' ? '#f59e0b' : '#94a3b8';
                        const StatusIcon = pl.status === 'published' ? FiCheckCircle : pl.status === 'cancelled' ? FiAlertCircle : pl.status === 'moved' ? FiRefreshCw : FiClock;
                        return (
                          <span key={j} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', background: `${c}18`, color: c, border: `1px solid ${c}30`, fontWeight: 500 }}>
                            {pl.name} <StatusIcon size={12} style={{ verticalAlign: 'middle', marginLeft: '3px' }} aria-hidden="true" />
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Calendar;
